import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  MenuLanguageCode,
  MenuLanguagePreference,
  MENU_LANGUAGE_CODES,
  normalizeMenuLanguage,
  resolveMenuLanguage
} from '../models/menu-language';

@Injectable({ providedIn: 'root' })
export class MenuLanguageService {
  private readonly preferenceKey = 'menuLanguagePreference';
  private readonly languageSubject = new BehaviorSubject<MenuLanguageCode>('es');

  readonly language$ = this.languageSubject.asObservable();

  get currentLanguage(): MenuLanguageCode {
    return this.languageSubject.value;
  }

  normalize(language?: string | null): MenuLanguageCode {
    return normalizeMenuLanguage(language);
  }

  detectBrowserLanguage(browser?: Pick<Navigator, 'languages' | 'language'> | null): MenuLanguageCode {
    const navigatorValue = browser === undefined
      ? (typeof navigator !== 'undefined' ? navigator : null)
      : browser;
    const firstLanguage = navigatorValue?.languages?.[0] || navigatorValue?.language;
    return this.normalize(firstLanguage);
  }

  getPreference(languageType?: number | string | null,
    browser?: Pick<Navigator, 'languages' | 'language'> | null): MenuLanguagePreference {
    const explicit = this.readExplicitPreference();
    if (explicit) return { preferredLanguage: explicit, preferenceSource: 'manual' };

    const numericLanguageType = typeof languageType === 'string'
      ? parseInt(languageType, 10)
      : languageType;
    if (numericLanguageType && numericLanguageType > 0) {
      return {
        preferredLanguage: resolveMenuLanguage(numericLanguageType),
        preferenceSource: 'profile'
      };
    }

    const detected = this.detectBrowserLanguage(browser);
    const hasBrowserLanguage = !!(browser === undefined
      ? (typeof navigator !== 'undefined' && (navigator.languages?.[0] || navigator.language))
      : (browser?.languages?.[0] || browser?.language));
    return {
      preferredLanguage: detected,
      preferenceSource: hasBrowserLanguage ? 'browser' : 'fallback'
    };
  }

  setExplicitPreference(language: MenuLanguageCode): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(this.preferenceKey, language);
    }
    this.activate(language);
  }

  activate(language?: string | null): MenuLanguageCode {
    const normalized = this.normalize(language);
    if (normalized !== this.languageSubject.value) this.languageSubject.next(normalized);
    return normalized;
  }

  initialize(languageType?: number | string | null,
    browser?: Pick<Navigator, 'languages' | 'language'> | null): MenuLanguagePreference {
    const preference = this.getPreference(languageType, browser);
    this.activate(preference.preferredLanguage);
    return preference;
  }

  readExplicitPreference(): MenuLanguageCode | null {
    if (typeof localStorage === 'undefined') return null;
    const stored = localStorage.getItem(this.preferenceKey);
    return stored && MENU_LANGUAGE_CODES.includes(stored as MenuLanguageCode)
      ? stored as MenuLanguageCode
      : null;
  }
}
